import { useState } from "react";
import Button from "../ui/Button.jsx";
import { useCreateEvent } from "../events/useCreateEvent.js";
import { getEvents } from "../services/apiEvents.js";
import { parseEventImport, validateEvent } from "../events/eventValidation.js";

function JsonEventsImporter() {
    const [jsonInput, setJsonInput] = useState("");
    const [parsedEvents, setParsedEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState(new Set());
    const [existingEventNames, setExistingEventNames] = useState(new Set());
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [parsing, setParsing] = useState(false);

    const { isCreating, createEventAsync } = useCreateEvent();
    const [savedIndexes, setSavedIndexes] = useState(new Set());
    const busy = submitting || parsing || isCreating;

    const handleParse = async () => {
        setParsing(true);
        try {
            const parsed = parseEventImport(jsonInput);

            // Fetch all existing events to check for duplicates
            const existingEvents = await getEvents();
            const existingNames = new Set(
                existingEvents.map(event => String(event.name || "").toLowerCase().trim())
            );
            setExistingEventNames(existingNames);

            setParsedEvents(parsed);
            setSavedIndexes(new Set());
            setSelectedEvents(new Set()); // Reset selections
            setError("");
        } catch (err) {
            setError("Invalid JSON: " + err.message);
            setParsedEvents([]);
            setSelectedEvents(new Set());
            setExistingEventNames(new Set());
        } finally {
            setParsing(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError("");
        try {
            // Validate all selected rows again after edits, before making any request.
            const rows = [...selectedEvents].filter(index => !savedIndexes.has(index))
                .map(index => ({ index, event: validateEvent(parsedEvents[index]) }));
            for (const { index, event } of rows) {
                await createEventAsync(event);
                setSavedIndexes(previous => new Set(previous).add(index));
                setSelectedEvents(previous => {
                    const next = new Set(previous);
                    next.delete(index);
                    return next;
                });
            }
        } catch (err) {
            setError(`Import stopped: ${err.message} Successfully saved rows will not be submitted again.`);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleEventSelection = (index) => {
        const newSelected = new Set(selectedEvents);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedEvents(newSelected);
    };

    const selectAll = () => {
        setSelectedEvents(new Set(parsedEvents.map((_, index) => index).filter(index => !savedIndexes.has(index))));
    };

    const deselectAll = () => {
        setSelectedEvents(new Set());
    };

    const handleReset = () => {
        setJsonInput("");
        setParsedEvents([]);
        setSelectedEvents(new Set());
        setExistingEventNames(new Set());
        setError("");
        setSavedIndexes(new Set());
    };

    const isEventAlreadyStored = (eventName) => {
        return existingEventNames.has(String(eventName || "").toLowerCase().trim());
    };

    const updateEventField = (index, field, value) => {
        const updatedEvents = [...parsedEvents];
        updatedEvents[index] = {
            ...updatedEvents[index],
            [field]: value
        };
        setParsedEvents(updatedEvents);
    };

    return (
        <div className="admin-tool space-y-4">
            <div className="admin-panel-intro">
                <div>
                    <h2>Import event JSON</h2>
                    <p>Paste an array, review each event, then select the records to create.</p>
                </div>
            </div>
            <textarea
                aria-label="Event JSON"
                disabled={busy}
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='Paste your JSON array here...'
                rows={8}
                className="w-full rounded-lg border bg-gray-50 p-3 font-mono"
            />

            <div className="flex flex-wrap items-center gap-2">
                <Button size="small" onClick={handleParse} disabled={busy}>
                    {parsing ? "Parsing..." : "Parse JSON"}
                </Button>
                <Button size="small" onClick={handleReset} disabled={busy}>
                    Reset
                </Button>

                            {parsedEvents.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="small" onClick={handleSubmit} disabled={busy || selectedEvents.size === 0}>
                        {submitting ? "Submitting..." : `Submit ${selectedEvents.size} Selected Events`}
                    </Button>
                    <Button size="small" onClick={selectAll} disabled={busy}>
                        Select All
                    </Button>
                    <Button size="small" onClick={deselectAll} disabled={busy}>
                        Deselect All
                    </Button>
                </div>
            )}
            </div>

            {error && (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">{error}</p>
            )}

            {parsedEvents.length > 0 && (
                <div className="space-y-3">
                    <p className="text-xs font-semibold text-green-700">
                        {parsedEvents.length} events parsed. {selectedEvents.size} selected.
                    </p>
                    {parsedEvents.map((event, index) => {
                        const isDuplicate = isEventAlreadyStored(event.name);
                        return (
                        <div
                            key={index}
                            className={`relative rounded-lg border p-3 transition-all ${
                                selectedEvents.has(index) ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-white'
                            } ${isDuplicate ? 'border-yellow-500 border-2' : ''}`}
                        >
                            <div className="flex items-start gap-3">
                                <input
                                    aria-label={`Select event ${index + 1}`}
                                    disabled={busy || savedIndexes.has(index)}
                                    type="checkbox"
                                    checked={selectedEvents.has(index)}
                                    onChange={() => toggleEventSelection(index)}
                                    className="mt-2"
                                />
                                <div className="min-w-0 flex-1 space-y-2">
                                    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                                        Event #{index + 1}
                                        {savedIndexes.has(index) && <span>Saved</span>}
                                        {isDuplicate && (
                                            <span className="text-yellow-600" title="This event name already exists in the database">
                                                ⚠️ Already stored
                                            </span>
                                        )}
                                    </h3>

                                    {Object.keys(event).map((key) => (
                                        <div key={key} className="grid grid-cols-1 gap-1 sm:grid-cols-4 sm:items-start sm:gap-2">
                                            <label className="pt-2 text-xs font-semibold text-gray-600">
                                                {key}:
                                            </label>
                                            {typeof event[key] === 'object' && event[key] !== null ? (
                                                <textarea
                                                    aria-label={`${key} for event ${index + 1}`}
                                                    disabled={busy || savedIndexes.has(index)}
                                                    value={JSON.stringify(event[key], null, 2)}
                                                    onChange={(e) => {
                                                        try {
                                                            const parsed = JSON.parse(e.target.value);
                                                            updateEventField(index, key, parsed);
                                                        } catch {
                                                            // Allow editing even if invalid JSON
                                                            updateEventField(index, key, e.target.value);
                                                        }
                                                    }}
                                                    className="col-span-3 p-2 border rounded font-mono text-sm"
                                                    rows={3}
                                                />
                                            ) : (
                                                <input
                                                    aria-label={`${key} for event ${index + 1}`}
                                                    disabled={busy || savedIndexes.has(index)}
                                                    type="text"
                                                    value={event[key] ?? ''}
                                                    onChange={(e) => updateEventField(index, key, e.target.value)}
                                                    className="col-span-3 p-2 border rounded"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default JsonEventsImporter;
